import { IS_DEV } from '@/constants/env';
import { applyRemoteDomain, markDomainReady } from '@/request/domain';
import { useAppStore } from '@/store/modules/app';

// 启动引导：客户端上下文 → 渠道信息 → 云控配置（下发接口/埋点域名）。
// 必须跑在任何业务请求之前，所有请求都在 http.ts 的拦截器里等域名确定。

let bootstrapPromise: Promise<void> | null = null;

// main.ts 调用一次；重复调用复用同一个 promise
export function startBootstrap() {
    if (!bootstrapPromise) bootstrapPromise = run();
    return bootstrapPromise;
}

// 依赖 ClientInfo / 云控配置的地方（如 App.vue）等这个
export function whenBootstrapped() {
    return startBootstrap();
}

// C++ 的回调不保证一定回来，超时按失败处理，避免整个启动流程卡死
function withTimeout<T>(promise: Promise<T>, ms: number, label: string) {
    return Promise.race([
        promise,
        new Promise<T>((_, reject) => {
            setTimeout(() => reject(new Error(`${label}超时 ${ms}ms`)), ms);
        }),
    ]);
}

async function run() {
    const appStore = useAppStore();

    // tips 客户端初始化（同步，保证挂载前 MyWin 已可用）
    appStore.SET_MyWin();

    // 窗口最大化/还原状态监听：紧跟客户端桥初始化，早于任何 await，避免漏掉启动初期的事件
    appStore.registerWindowStateListener();

    // 请求c++获取渠道等信息（云控按渠道/版本下发，必须先于配置请求）
    try {
        const channelResult: any = await withTimeout(
            appStore.MyWin.WorkAiApi('get_client_channel', ''),
            3000,
            '[启动] 获取渠道信息'
        );

        if (channelResult?.code == 200) appStore.SET_ClientInfo({
            ...channelResult.data,
            client_channel: IS_DEV ? 'workai_test01_test01' : channelResult.data.client_channel
        });  // 存储配置信息
    } catch (err) {
        console.error('[启动] 获取渠道信息失败', err);
    }

    // 拉云控配置：下发接口域名与埋点域名，整份配置同时存进 AppStore.AppConfig
    try {
        const config: any = await appStore.FETCH_AppConfig({ bootstrap: true });

        if (config?.domain) applyRemoteDomain(config.domain);
        else console.warn('[启动] 云控配置未下发域名，沿用 env 默认值', config);
    } finally {
        // 兜底放行：不调用的话，等待域名的请求会一直挂起
        markDomainReady();
    }
}
